import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { Field } from 'react-final-form';
import { FormLegend } from '@unite-us/ui';
import {
  InputField,
  DateField,
  SimpleSelectField,
} from '@unite-us/app-components';
import today from 'common/utils/today';
import { required, validateSSN } from 'src/common/form/utils/rffValidations';

// Sub-component for Name Fields
function NameFields() {
  return (
    <div className="row">
      <div className="col-xs-12 col-sm-6">
        <Field name="first_name" validate={required}>
          {(params) => (
            <InputField
              {...params}
              label="First Name"
              id="first-name"
              autoComplete="given-name"
              required
            />
          )}
        </Field>
      </div>

      <div className="col-xs-12 col-sm-6">
        <Field name="last_name" validate={required}>
          {(params) => (
            <InputField
              {...params}
              label="Last Name"
              id="last-name"
              autoComplete="family-name"
              required
            />
          )}
        </Field>
      </div>
    </div>
  );
}

// Sub-component for Demographics Fields
function DemographicsFields({ peopleEnums }) {
  return (
    <div className="row">
      <div className="col-xs-12">
        <Field name="gender" validate={required}>
          {({ input, meta }) => (
            <SimpleSelectField
              className="basic-information-gender"
              id="gender"
              label="Gender"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={peopleEnums.genders}
              optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
              error={meta.touched ? meta.error : undefined}
              required
            />
          )}
        </Field>
        <Field name="marital_status" validate={required}>
          {({ input, meta }) => (
            <SimpleSelectField
              className="basic-information-marital-status"
              id="marital-status"
              label="Marital Status"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={peopleEnums.marital_status}
              optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
              error={meta.touched ? meta.error : undefined}
              required
            />
          )}
        </Field>
        <Field name="race" validate={required}>
          {({ input, meta }) => (
            <SimpleSelectField
              className="basic-information-race"
              id="race"
              label="Race"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={peopleEnums.race}
              optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
              error={meta.touched ? meta.error : undefined}
              required
            />
          )}
        </Field>
        <Field name="ethnicity" validate={required}>
          {({ input, meta }) => (
            <SimpleSelectField
              className="basic-information-ethnicity"
              id="ethnicity"
              label="Ethnicity"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={peopleEnums.ethnicity}
              optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
              error={meta.touched ? meta.error : undefined}
              required
            />
          )}
        </Field>
      </div>
    </div>
  );
}

DemographicsFields.propTypes = {
  peopleEnums: PropTypes.object.isRequired,
};

function BasicInformation({ enums }) {
  const peopleEnums = _.wget(enums, 'people', {});

  return (
    <div className="basic-information">
      <h3 className="basic-information__title">Personal Information</h3>
      <FormLegend />

      <hr />

      <NameFields />

      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name="date_of_birth" validate={required}>
            {({ input, meta }) => (
              <DateField
                {...input}
                label="Date of Birth"
                id="date-of-birth"
                maxDate={today()}
                required
                error={meta.touched ? meta.error : undefined}
              />
            )}
          </Field>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-6">
          <Field name="ssn" validate={validateSSN}>
            {({ input, meta }) => (
              <InputField
                {...input}
                id="social-security-number"
                className="basic-information__social-security-number"
                label="Social Security Number"
                mask="999-99-9999"
                autoComplete="off"
                error={meta.touched ? meta.error : undefined}
              />
            )}
          </Field>
        </div>
      </div>

      <DemographicsFields peopleEnums={peopleEnums} />
    </div>
  );
}

BasicInformation.propTypes = {
  enums: PropTypes.object.isRequired,
};

BasicInformation.fields = [
  'first_name',
  'last_name',
  'date_of_birth',
  'gender',
  'marital_status',
  'race',
  'ethnicity',
  'ssn',
  'ssn_last4',
];

export default BasicInformation;
